/**
 * =============================================================================
 * ROASTBREW — MATHLIVE SETUP
 * =============================================================================
 *
 * The ONLY module that imports `mathlive`. Everything else talks to MathLive
 * through the functions below, so there is one place to change if the library's
 * surface moves — and one place that reports whether it loaded at all.
 *
 * Importing this module registers the <math-field> custom element as a side
 * effect. AppLayout imports it, so the element is defined before any page
 * mounts.
 *
 * ── THE VIRTUAL KEYBOARD IS NOT CONFIGURED HERE ────────────────────────────
 * All of it was removed and will be rebuilt from scratch: the custom layouts,
 * the tab strip, the docking container, the show/hide plumbing and the panel
 * stylesheet. MathLive's own default keyboard applies until then, which on a
 * touch device is its stock behaviour and on a desktop is nothing.
 *
 * What remains below is only what a MATHFIELD needs: the delimiters used when
 * rendering static maths, and the handful of CSS variables that control the
 * caret and the selection inside an editable field.
 * =============================================================================
 */

import { useEffect } from 'react';
import { MathfieldElement, renderMathInElement } from 'mathlive';
import { PANEL } from './panelTheme';

// ── DELIMITERS ──────────────────────────────────────────────────────────────
// MathLive's static renderer recognises \(…\), \[…\] and $$…$$ by default, and
// deliberately NOT $…$ — "it costs $5 to $8" would otherwise typeset "5 to".
//
// WITHOUT THIS OBJECT every card line prints its dollar signs literally and
// nothing is typeset. Display math stays at $$…$$: a card line is always
// inline, and leaving that default alone means a stray $$ behaves the way
// anyone reading the LaTeX would expect.
export const MATH_RENDER_OPTIONS = {
  TeX: {
    delimiters: {
      inline: [['$', '$']],
      display: [['$$', '$$']],
    },
  },
};

/**
 * Set an element's text and typeset the $…$ spans inside it.
 *
 * textContent is set FIRST so the string is always treated as text and never
 * as markup — transcribed content is model output and must not be able to
 * inject HTML.
 *
 * Returns true if typesetting ran. False means `renderMathInElement` was
 * absent, which is the mathlive-ssr.min.mjs resolution problem rather than
 * anything to do with the content.
 */
export function renderMathIn(el, content) {
  if (!el) return false;
  el.textContent = content ?? '';
  if (typeof renderMathInElement !== 'function') return false;
  try {
    renderMathInElement(el, MATH_RENDER_OPTIONS);
    return true;
  } catch (err) {
    // Leave the plain text in place. Unrendered LaTeX is readable; a blank
    // line is not.
    console.error('[mathlive] render failed:', err);
    return false;
  }
}

/**
 * Whether the library actually arrived, and in what shape.
 *
 * Lives here rather than importing MathfieldElement at a call site, so this
 * module stays the single point of contact with the library.
 */
export function mathLiveDiagnostics() {
  return {
    version: MathfieldElement?.version ?? 'unavailable',
    registered:
      typeof customElements !== 'undefined' && !!customElements.get('math-field'),
    canRender: typeof renderMathInElement === 'function',
    keyboard: typeof window !== 'undefined' && !!window.mathVirtualKeyboard,
  };
}

// ── MATHFIELD APPEARANCE ────────────────────────────────────────────────────
/**
 * Map PANEL onto the CSS custom properties that affect an editable mathfield.
 *
 * These go on <body> rather than on a component because custom properties
 * inherit, so one declaration reaches every mathfield in the app.
 *
 * ONLY THE FIELD. Nothing here touches the virtual keyboard.
 */
function fieldVars() {
  return {
    '--caret-color': PANEL.accent,
    '--primary-color': PANEL.accent,
    '--selection-background-color': PANEL.accentBg,
    '--selection-color': PANEL.text,
    '--smart-fence-color': PANEL.muted,

    // THE CLICK HIGHLIGHT. MathLive paints this behind whatever group contains
    // the caret, so clicking into a numerator lights up the whole fraction.
    // Transparent leaves the caret as the only indicator of position.
    '--contains-highlight-background-color': 'transparent',
  };
}

/**
 * Configure MathLive for the whole app. Call ONCE, from AppLayout.
 *
 * Takes no arguments. It used to take the route, to collapse the keyboard on
 * navigation — that went with the rest of the keyboard code.
 */
export function useMathLive() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const vars = fieldVars();
    Object.entries(vars).forEach(([k, v]) => {
      if (v) document.body.style.setProperty(k, v);
    });

    return () => {
      Object.keys(vars).forEach((k) => document.body.style.removeProperty(k));
    };
  }, []);
}
